'use client';

import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/context/LanguageContext';

interface ServiceItem {
  number: string;
  category: string;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  features: string[];
}

export default function ServicesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();

  const services: ServiceItem[] = (t('services.items') as unknown as ServiceItem[]) || [];

  // Parse from JSON since t() returns string for leaf nodes
  const getServices = (): ServiceItem[] => {
    try {
      const items = [];
      for (let i = 0; i < 3; i++) {
        items.push({
          number: t(`services.items.${i}.number`),
          category: t(`services.items.${i}.category`),
          title: t(`services.items.${i}.title`),
          subtitle: t(`services.items.${i}.subtitle`),
          description: t(`services.items.${i}.description`),
          icon: t(`services.items.${i}.icon`),
          features: [
            t(`services.items.${i}.features.0`),
            t(`services.items.${i}.features.1`),
            t(`services.items.${i}.features.2`),
            t(`services.items.${i}.features.3`),
          ],
        });
      }
      return items;
    } catch {
      return [];
    }
  };

  const serviceItems = getServices();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1 }
    );
    const cards = sectionRef.current?.querySelectorAll('.reveal');
    cards?.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-blackout relative overflow-hidden" ref={sectionRef}>
      {/* Background accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-transparent via-gold/30 to-transparent" />

      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-12 sm:mb-16 md:mb-20 reveal">
          <span className="text-gold text-xs font-semibold uppercase tracking-[0.3em] mb-3 sm:mb-4 block">{t('services.label')}</span>
          <h2 className="text-3xl sm:text-4xl md:text-display font-black text-monitor mb-3 sm:mb-4">{t('services.title')}</h2>
          <p className="text-monitor/40 text-sm sm:text-base max-w-xl mx-auto px-2 sm:px-0">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Service cards */}
        <div className="space-y-4 sm:space-y-6">
          {serviceItems.map((service, index) => (
            <div
              key={service.number}
              className="reveal group relative border border-white/5 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 bg-charcoal/20 hover:bg-charcoal/40 hover:border-gold/20 transition-all duration-500 overflow-hidden"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              {/* Number watermark */}
              <span className="absolute right-4 sm:right-8 top-4 sm:top-6 text-4xl sm:text-5xl md:text-6xl font-black text-white/[0.03] leading-none select-none pointer-events-none">
                {service.number}
              </span>

              {/* Gold accent line */}
              <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative z-10 grid md:grid-cols-[1fr_auto] gap-6 sm:gap-8 items-start">
                <div>
                  {/* Category tag */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-gold/60 text-xs font-mono uppercase tracking-widest">{service.number}</span>
                    <span className="w-8 h-px bg-gold/30" />
                    <span className="text-gold/60 text-xs font-semibold uppercase tracking-wider">{service.category}</span>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-monitor mb-2 group-hover:text-gold transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gold/70 text-xs sm:text-sm font-medium mb-4">{service.subtitle}</p>

                  {/* Description */}
                  <p className="text-monitor/50 text-sm sm:text-base leading-relaxed max-w-2xl">{service.description}</p>
                </div>

                {/* Feature list */}
                <div className="md:min-w-[220px] w-full md:w-auto">
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2.5 text-xs sm:text-sm text-monitor/40 group-hover:text-monitor/60 transition-colors duration-300">
                        <span className="w-1 h-1 rounded-full bg-gold/50 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
